import React, { FC, ReactNode, useState } from "react";
import { Autocomplete, TextField, Chip, Tooltip } from "@mui/material";
import { deepDerefrencer, IFormField } from "../../../utils/form_factory";
import { ukoTheme } from "../../../theme";
import Zoom from "@mui/material/Zoom";

interface IFormMultiSelectEmailProps extends Partial<IFormField> {
  name: string;
  label: any;
  type?: string;
  formControl: any;
  hint?: ReactNode | string;
  isDisabled?: boolean;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FormMultiSelectEmail: FC<IFormMultiSelectEmailProps> = ({
  name,
  label,
  type,
  hint,
  formControl,
}: IFormMultiSelectEmailProps) => {
  const theme = ukoTheme();
  const [inputValue, setInputValue] = useState("");
  const isFieldRequired = (fieldName: string) => {
    return String(formControl.getFieldMeta(fieldName)?.error).match(
      /required/gi,
    )
      ? true
      : false;
  };

  const currentValues = Array.isArray(formControl.values[name])
    ? formControl.values[name]
    : [];

  const processAndAddEmails = (text: any) => {
    if (!text) return;

    const rawEmails = text.split(/[,;]+/);
    const newEmails = rawEmails
      .map((email: any) => email.trim())
      .filter((email: any) => EMAIL_REGEX.test(email));

    if (newEmails.length > 0) {
      const updatedEmails = new Set([...currentValues, ...newEmails]);
      formControl.setFieldValue(name, updatedEmails);
    }
    setInputValue("");
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      {/* CC Input */}
      <Tooltip
        title={
          hint ||
          "Press enter for the email to be added. Multiple emails are also enabled"
        }
        slots={{
          transition: Zoom,
        }}
      >
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={currentValues}
          inputValue={inputValue}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === "input") {
              if (newInputValue.includes(",") || newInputValue.includes(";")) {
                processAndAddEmails(newInputValue);
              } else {
                setInputValue(newInputValue);
              }
            } else if (reason === "clear") {
              setInputValue("");
            }
          }}
          onChange={(event, newValue) => {
            // Filter the incoming array from MUI to ensure no bad text sneaks through freeSolo bypasses
            const validOnly = newValue.filter((email) =>
              EMAIL_REGEX.test(email),
            );
            formControl.setFieldValue(name, validOnly);
          }}
          renderTags={(value, getTagProps) =>
            Array.isArray(value)
              ? value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))
              : null
          }
          renderInput={(params) => (
            <TextField
              {...params}
              sx={[
                styles.customStyles(
                  theme,
                  isFieldRequired(name)
                    ? theme.palette.primary.main
                    : undefined,
                ),
              ]}
              name={name}
              label={label}
              error={Boolean(
                deepDerefrencer(formControl.touched, name) &&
                  deepDerefrencer(formControl.errors, name),
              )}
              helperText={
                deepDerefrencer(formControl.touched, name) &&
                deepDerefrencer(formControl.touched, name)
              }
              onBlur={(e) => {
                formControl.handleBlur(e);
                processAndAddEmails(inputValue); // Convert remaining text to chip on blur
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  processAndAddEmails(inputValue);
                }
              }}
            />
          )}
        />
      </Tooltip>
    </div>
  );
};

const styles = {
  customStyles: (theme: any, outlineColor?: string) => ({
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: "8px",
      border: "2px solid",
      borderColor: outlineColor
        ? outlineColor
        : theme.palette.mode === "light"
        ? theme.palette.secondary[300]
        : theme.palette.divider,
    },

    "& input[type='file'].MuiInputBase-input": {
      marginLeft: "120px",
    },
  }),
};

export default FormMultiSelectEmail;
